/**
 * Jarvis: Geräteschlüssel des Browsers (WebCrypto, ECDSA P-256, NICHT exportierbar).
 * Der private Schlüssel verlässt den Browser nie; der Server kennt nur den öffentlichen Teil
 * und prüft damit Signaturen. Liegt in IndexedDB dieses Browsers (nicht kopierbar).
 */
const DB = "jarvis-device"
const STORE = "keys"
const KEY = "device-key-v1"

function b64u(buf: ArrayBuffer): string {
  let s = ""
  const a = new Uint8Array(buf)
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i])
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1)
    r.onupgradeneeded = () => r.result.createObjectStore(STORE)
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

function idb<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode)
        const req = fn(tx.objectStore(STORE))
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      }),
  )
}

export function deviceKeySupported(): boolean {
  return typeof crypto !== "undefined" && !!crypto.subtle && typeof indexedDB !== "undefined"
}

export async function getDeviceKey(): Promise<{ priv: CryptoKey; pub: CryptoKey }> {
  if (!deviceKeySupported()) throw new Error("device_key_unsupported")
  const saved = await idb<{ priv: CryptoKey; pub: CryptoKey } | undefined>("readonly", (s) => s.get(KEY))
  if (saved) return saved
  const pair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"])
  const rec = { priv: pair.privateKey, pub: pair.publicKey }
  await idb("readwrite", (s) => s.put(rec, KEY))
  return rec
}

export async function devicePublicKey(): Promise<string> {
  const { pub } = await getDeviceKey()
  return b64u(await crypto.subtle.exportKey("spki", pub))
}

export async function deviceSign(message: string): Promise<string> {
  const { priv } = await getDeviceKey()
  const sig = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, priv, new TextEncoder().encode(message))
  return b64u(sig) // P1363 (r||s), 64 Bytes
}

/** Nur zur Anzeige für den Eigentümer; die Bindung geschieht über den Schlüssel, nicht über diese Texte. */
export function describeDevice(): { type: string; browser: string } {
  const ua = navigator.userAgent || ""
  const type = /iPad|Tablet/i.test(ua) ? "Планшет" : /Mobi|Android|iPhone/i.test(ua) ? "Телефон" : "Компьютер"
  const br = /Edg\//.test(ua) ? "Edge" : /OPR\//.test(ua) ? "Opera" : /Firefox\//.test(ua) ? "Firefox"
    : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Браузер"
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad|iOS/.test(ua) ? "iOS"
    : /Mac OS X/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : ""
  return { type, browser: os ? `${br} / ${os}` : br }
}
